import { ListData } from '@fi-app/components/items-list/list-data.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import {
    ItemsListElementType,
    ItemsListHeader,
    ItemsListHeaderProperties,
} from '@fi-app/components/items-list/items-list-header';
import { ChecklistListItemColourIndicatorComponent } from './item-colour-indicator/item-colour-indicator.component';
import { ChecklistListItemDueDateTimeComponent } from './item-due-date-time/item-due-date-time.component';
import { ChecklistLitHeadItemColourIndicator } from './head-item-colour-indicator/head-item-colour-indicator.component';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ChecklistService } from '../checklist.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ChecklistModel } from '../checklist.model';
import { Injector } from '@angular/core';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IFiChecklistModelBase } from '@fi-shared/checklist';
import { IItemsListAction } from '@fi-app/components/items-list/items-list';
import { FiDateTimeFormatOptions } from '@fi-shared/utils';

export class ChecklistListDataModel extends ListData<ChecklistModel> {
    constructor(
        injector: Injector,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private loadingService: LoadingService,
        private checklistService: ChecklistService,
        private detectDevices: DetectDevicesService,
        isLocalSort?: boolean,
        defaultColumn?: string,
        defaultReverse?: boolean,
        extendedSearchMode?: boolean,
    ) {
        super(injector, isLocalSort, 'DueDateTime', false);

        this.ObjectType = ObjectTypes.CheckList;
        this.DeletePermanentlyTextId = 'checklist.delete_permanently_confirm';
        this.ShowCheckBox = !!extendedSearchMode || this.detectDevices.isMobilePhone();
        this.MultiSelect = false;
        this.ShowDelete = false;

        this.ShowFavoriteIcon = false;

        this.IconClass = 'fi-icon-checklist';

        this._initHeadItems(extendedSearchMode);
    }

    getRowClass(checklist: ChecklistModel) {
        const result = [];

        if (checklist.IsSetNotApplicable) result.push('disabled-row');

        if (!checklist.IsVisited) result.push('bold');

        return result.join(' ');
    }

    _initHeadItems(extendedSearchMode?: boolean) {
        this.Header.push(
            new ItemsListHeader(
                '',
                'AnswersStatus',
                new ItemsListHeaderProperties(
                    'fi-checklist-list-item-colour-indicator CcmListItem-NoBorder',
                    true,
                    ChecklistListItemColourIndicatorComponent,
                    {
                        headLabelType: ChecklistLitHeadItemColourIndicator,
                    },
                ),
            ),
        );

        this.Header.push(
            new ItemsListHeader(
                '',
                '',
                new ItemsListHeaderProperties(
                    'CcmListItem-Icon CcmListItem-NoBorder ccmListViewHiddenCell',
                    true,
                    ItemsListElementType.ImgIconA,
                    {
                        Sref: () => {
                            return FiStateNames.Checklist.View;
                        },
                        SrefParams: (data) => {
                            return { Id: data.Id, filterType: this.stateService.params.filterType };
                        },
                    },
                ),
            ),
        );

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.name'),
                'Name',
                new ItemsListHeaderProperties(
                    'CcmListItem-Name',
                    true,
                    extendedSearchMode ? ItemsListElementType.SpanPlain : ItemsListElementType.A,
                    {
                        Sref: FiStateNames.Checklist.View,
                        SrefParams: (data) => {
                            return { Id: data.Id, filterType: this.stateService.params.filterType };
                        },
                        GetClass: (data: ChecklistModel) => {
                            const classes: string[] = [];
                            if (data) {
                                if (!data.IsActive) classes.push('LightColor');
                            }
                            return classes.join(' ');
                        },
                        onClick: ($event, data: ChecklistModel) => {
                            const mobileAppHelperService = this.injector.get(FiMobileAppHelperService);
                            if (mobileAppHelperService.mobileAppMode) {
                                $event.preventDefault();
                                mobileAppHelperService.openChecklistViewer(data.Id, data.Name);
                            }
                        },
                        /* mobileSubText: function (data) {
                         return self.Sort.Column == 'DueDateTime' ? data.DueDateTimeStr : data.StartDateTimeStr;
                     },*/
                    },
                ),
            ),
        );

        this.addSpanPlainHeader(
            this.appTextService.get('checklist.checkpoints'),
            'CheckPointsCount',
            '',
            true,
            (data) => data.CheckPointsCount,
        );

        this.addSpanPlainHeader(
            this.appTextService.get('start_date_time'),
            'StartDateTime',
            'Ccm-Date',
            true,
            (data) => {
                return this.fiFormatDateService.formatDate(data['StartDateTimeStr'], FiDateTimeFormatOptions, {
                    showToday: false,
                    showUTC: true,
                });
            },
        );

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('due_date_time'),
                'DueDateTime',
                new ItemsListHeaderProperties(
                    'Ccm-Date CcmList-Column-ShowAlways',
                    true,
                    ChecklistListItemDueDateTimeComponent,
                ),
                undefined,
                undefined,
                (data) => {
                    return this.fiFormatDateService.formatDate(data['DueDateTimeStr'], FiDateTimeFormatOptions, {
                        showToday: false,
                        showUTC: true,
                    });
                },
            ),
        );

        this.addChangedOnHeader();

        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.ChecklistLocal,
                AccessRightType.Write,
            ) &&
            !extendedSearchMode
        ) {
            this._initActions();
        }
    }

    _initActions() {
        const allowedActions = this.checklistService.getUserAllowedActions();
        const actions: IItemsListAction<ChecklistModel>[] = [
            allowedActions.editChecklist,
            allowedActions.viewReports,
            allowedActions.saveAsTemplate,
            allowedActions.turnOffReminder,
            allowedActions.setNotApplicable,
            allowedActions.deleteResponses,
            allowedActions.deleteChecklist,
        ];

        const deleteResponsesOnClick = allowedActions.deleteResponses.onClick;
        allowedActions.deleteResponses.onClick = (checklist) => {
            return deleteResponsesOnClick(checklist).then(() => {
                const apiModel = {
                    SelectedSort: {
                        ColumnName: this.Sort.Column,
                        SortDirection: this.Sort.Reverse,
                    },
                    Offset: 0,
                    NumberOfRecordsToTake: 1,
                    Names: [],
                    Ids: [],
                    FilterType: this.FilterType,
                };
                apiModel.Ids.push(checklist.Id);
                this.loadingService.trackPromise(this.checklistService.getCheckLists(apiModel)).then((result) => {
                    Object.assign(checklist, result[0]);
                    this.Header[0].Properties.updateContent$.next();
                });
            });
        };

        allowedActions.deleteChecklist.onClick = ((deleteChecklistOnClick) => {
            return (checkListToDelete: IFiChecklistModelBase) => {
                return deleteChecklistOnClick(checkListToDelete).then(() => {
                    this.RemoveFromList([checkListToDelete]);
                });
            };
        })(allowedActions.deleteChecklist.onClick);

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.actions'),
                'Actions',
                new ItemsListHeaderProperties(
                    'CcmListItem-Actions CcmList-Column-ShowBreakMobile',
                    false,
                    ItemsListElementType.Actions,
                    { actions: actions },
                ),
            ),
        );

        const self = this;

        const mobileAllowedActions = <any>this.checklistService.getUserAllowedActions();
        const mobileActions = [
            mobileAllowedActions.setNotApplicable,
            mobileAllowedActions.turnOffReminder,
            mobileAllowedActions.viewReports,
            mobileAllowedActions.saveAsTemplate,
            mobileAllowedActions.editChecklist,
            mobileAllowedActions.deleteResponses,
            mobileAllowedActions.deleteChecklist,
        ];
        mobileActions.forEach((action) => {
            action.headerText = action.title;

            action.condition = ((condition) => {
                return () => {
                    const item = self.SelectedItems.length ? self.SelectedItems[0] : null;
                    return !item || !condition ? true : condition(item);
                };
            })(action.condition);

            action.isDisabled = ((isDisabled) => {
                return () => {
                    const item = self.SelectedItems.length ? self.SelectedItems[0] : null;
                    if (!item) return true;
                    return isDisabled ? isDisabled(item) : false;
                };
            })(action.isDisabled);

            action.onClick = ((onClick) => {
                return () => {
                    const item = self.SelectedItems.length ? self.SelectedItems[0] : null;
                    if (item) {
                        onClick(item);
                        self.MobileMoreOpened = false;
                    }
                };
            })(action.onClick);

            action.isChecked = ((isChecked) => {
                return () => {
                    const item = self.SelectedItems.length ? self.SelectedItems[0] : null;
                    if (!item) return false;
                    return isChecked ? isChecked(item) : false;
                };
            })(action.isChecked);
        });

        this.moreMenuMobileItems = [
            {
                title: this.appTextService
                    .get('list.mobile.sort_by')
                    .format(this.appTextService.get('common.status').toLowerCase()),
                condition: () => {
                    return true;
                },
                isDisabled: () => {
                    return self.Sort.Column == 'AnswersStatus' && self.Sort.Reverse == true;
                },
                onClick: () => {
                    self.Sort.Column = 'AnswersStatus';
                    self.Sort.Reverse = true;
                    self.MobileMoreOpened = false;
                    self.raiseSortChanged();
                },
            },
            {
                title: this.appTextService
                    .get('list.mobile.sort_by')
                    .format(this.appTextService.get('common.end_date').toLowerCase()),
                condition: () => {
                    return true;
                },
                isDisabled: () => {
                    return self.Sort.Column == 'DueDateTime' && self.Sort.Reverse == false;
                },
                onClick: () => {
                    self.Sort.Column = 'DueDateTime';
                    self.Sort.Reverse = false;
                    self.MobileMoreOpened = false;
                    self.raiseSortChanged();
                },
            },
            {
                title: this.appTextService
                    .get('list.mobile.sort_by')
                    .format(this.appTextService.get('common.name').toLowerCase()),
                condition: () => {
                    return true;
                },
                isDisabled: () => {
                    return self.Sort.Column == 'Name' && self.Sort.Reverse === false;
                },
                onClick: () => {
                    self.Sort.Column = 'Name';
                    self.Sort.Reverse = false;
                    self.MobileMoreOpened = false;
                    self.raiseSortChanged();
                },
            },
        ].concat(mobileActions);
    }
}
